import { roundBookingCurrency } from './bookingPriceCalculation';

/** GST rule types for extra charge reasons */
export const EXTRA_CHARGE_GST_RULES = {
  laundry: { type: 'fixed', percent: 18, label: '18% (laundry services)' },
  dry_cleaning: { type: 'fixed', percent: 18, label: '18% (dry cleaning)' },
  room_extras: { type: 'room_gst', label: 'Same as room GST' },
  transport: {
    type: 'options',
    options: [5, 12, 18],
    defaultPercent: 5,
    label: 'Transport GST (5% / 12% / 18%)',
  },
  damage: {
    type: 'optional_none',
    defaultPercent: 0,
    label: 'No GST (damage / penalty)',
  },
  food_beverage: { type: 'options', options: [5, 18], defaultPercent: 5, label: 'F&B GST (5% / 18%)' },
  default: { type: 'options', options: [0, 5, 12, 18], defaultPercent: 5, label: 'GST rate' },
};

export const QUICK_REASON_GST_KEY = {
  'Extra mattress': 'room_extras',
  'Extra bed': 'room_extras',
  'Extra pillow / linen': 'room_extras',
  'Extra guest': 'room_extras',
  'Baby cot': 'room_extras',
  'Late checkout': 'default',
  'Early check-in': 'default',
  'Room service': 'food_beverage',
  'Minibar': 'food_beverage',
  'Food & beverage': 'food_beverage',
  'Laundry': 'laundry',
  'Dry cleaning': 'dry_cleaning',
  'Parking': 'default',
  'Wi-Fi / internet': 'default',
  'Airport transfer': 'transport',
  'Cab / transport': 'transport',
  'Pet fee': 'default',
  'Smoking fine': 'damage',
  'TV / appliance damage': 'damage',
  'Room damage': 'damage',
  'Lost key / card': 'damage',
  'Missing items': 'damage',
  'Spa / wellness': 'default',
  'Doctor / medicine': 'default',
  'Conference / meeting': 'default',
};

function parseReasonTokens(text) {
  return (text || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function getGstRuleKeyForReason(reason) {
  if (!reason) return 'default';
  if (QUICK_REASON_GST_KEY[reason]) return QUICK_REASON_GST_KEY[reason];
  const lower = reason.toLowerCase();
  if (lower.includes('laundry')) return 'laundry';
  if (lower.includes('dry clean')) return 'dry_cleaning';
  if (lower.includes('damage') || lower.includes('smoking') || lower.includes('lost key') || lower.includes('missing')) {
    return 'damage';
  }
  if (lower.includes('airport') || lower.includes('cab') || lower.includes('transport')) return 'transport';
  if (lower.includes('bed') || lower.includes('mattress') || lower.includes('cot') || lower.includes('pillow')) {
    return 'room_extras';
  }
  if (lower.includes('food') || lower.includes('minibar') || lower.includes('room service')) return 'food_beverage';
  return 'default';
}

export function resolveExtraChargeGstDefaults({ note = '', lastReason = null, roomGstPercent = 0 } = {}) {
  const tokens = parseReasonTokens(note);
  const activeReason = lastReason || tokens[tokens.length - 1] || '';
  const ruleKey = getGstRuleKeyForReason(activeReason);
  const rule = EXTRA_CHARGE_GST_RULES[ruleKey] || EXTRA_CHARGE_GST_RULES.default;
  const roomPct = roundBookingCurrency(roomGstPercent);

  if (rule.type === 'fixed') {
    return {
      ruleKey,
      rule,
      applyGst: true,
      gstPercent: rule.percent,
      options: [rule.percent],
      helperText: rule.label,
    };
  }

  if (rule.type === 'room_gst') {
    const pct = roomPct > 0 ? roomPct : 5;
    return {
      ruleKey,
      rule,
      applyGst: true,
      gstPercent: pct,
      options: roomPct > 0 ? [roomPct, 5, 12, 18] : [5, 12, 18],
      helperText: roomPct > 0 ? `Same as room GST (${roomPct}%)` : 'Same as room GST (select rate)',
    };
  }

  if (rule.type === 'optional_none') {
    return {
      ruleKey,
      rule,
      applyGst: false,
      gstPercent: 0,
      options: [0, 5, 12, 18],
      helperText: rule.label,
    };
  }

  return {
    ruleKey,
    rule,
    applyGst: (rule.defaultPercent || 0) > 0,
    gstPercent: rule.defaultPercent || 0,
    options: rule.options || [0, 5, 12, 18],
    helperText: rule.label,
  };
}

export function calculateExtraChargePreview(baseAmount, gstPercent, applyGst) {
  const base = roundBookingCurrency(baseAmount);
  const pct = applyGst ? roundBookingCurrency(gstPercent) : 0;
  const gstAmount = roundBookingCurrency((base * pct) / 100);
  const total = roundBookingCurrency(base + gstAmount);
  return { base, gstPercent: pct, gstAmount, total };
}

export function parseGstPercentInput(value) {
  const parsed = parseFloat(String(value ?? '').replace(/[^\d.]/g, ''));
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(100, Math.max(0, roundBookingCurrency(parsed)));
}

export function matchesPresetGstPercent(percent, options = []) {
  const target = roundBookingCurrency(percent);
  return (options || []).some((option) => roundBookingCurrency(option) === target);
}

export function normalizeExtraChargeItem(item, fallbackReason = 'Extra Charges', itemIndex = null) {
  let base = roundBookingCurrency(item?.base_amount ?? item?.baseAmount ?? 0);
  const gstPercent = roundBookingCurrency(item?.gst_percent ?? item?.gstPercent ?? 0);
  let gstAmount = roundBookingCurrency(item?.gst_amount ?? item?.gstAmount ?? 0);
  let total = roundBookingCurrency(item?.total_amount ?? item?.totalAmount ?? item?.total ?? 0);
  if (total <= 0 && base > 0) {
    total = roundBookingCurrency(base + gstAmount);
  }
  if (gstAmount <= 0 && base > 0 && gstPercent > 0) {
    gstAmount = roundBookingCurrency((base * gstPercent) / 100);
    if (total <= base) total = roundBookingCurrency(base + gstAmount);
  }
  if (total > 0 && base <= 0) {
    base = roundBookingCurrency(Math.max(0, total - gstAmount));
  }
  return {
    id: item?.id || null,
    itemIndex,
    reason: (item?.reason || item?.label || fallbackReason || 'Extra Charges').trim() || 'Extra Charges',
    base,
    gstPercent,
    gstAmount,
    total: total > 0 ? total : base,
    isPending: Boolean(item?.isPending),
  };
}

export function aggregateExtraChargeRows(rows = []) {
  return (rows || []).reduce(
    (acc, row) => ({
      base: roundBookingCurrency(acc.base + (row?.base || 0)),
      gstAmount: roundBookingCurrency(acc.gstAmount + (row?.gstAmount || 0)),
      total: roundBookingCurrency(acc.total + (row?.total || 0)),
    }),
    { base: 0, gstAmount: 0, total: 0 }
  );
}

export function buildExtraChargesSummary({
  existingItems = [],
  existingAmount = 0,
  existingBaseAmount = 0,
  existingGstAmount = 0,
  existingNote = '',
  pendingReason = '',
  pendingPreview = null,
} = {}) {
  let existingRows = (existingItems || [])
    .map((item, index) => normalizeExtraChargeItem(item, 'Extra Charges', index))
    .filter((row) => row.total > 0 || row.base > 0);

  if (!existingRows.length && Number(existingAmount) > 0) {
    const total = roundBookingCurrency(existingAmount);
    const base = roundBookingCurrency(existingBaseAmount || total);
    const gstAmount = roundBookingCurrency(
      existingGstAmount || Math.max(0, total - base)
    );
    existingRows = [
      normalizeExtraChargeItem(
        {
        reason: existingNote || 'Extra Charges',
        base_amount: base,
        gst_amount: gstAmount,
        total_amount: total,
        gst_percent: base > 0 ? roundBookingCurrency((gstAmount / base) * 100) : 0,
        },
        'Extra Charges',
        0,
      ),
    ];
  }

  const pendingRow =
    pendingPreview && pendingPreview.base > 0
      ? normalizeExtraChargeItem(
          {
            reason: pendingReason || 'New charge',
            base_amount: pendingPreview.base,
            gst_percent: pendingPreview.gstPercent,
            gst_amount: pendingPreview.gstAmount,
            total_amount: pendingPreview.total,
            isPending: true,
          },
          'New charge',
          null
        )
      : null;

  const existingTotals = aggregateExtraChargeRows(existingRows);
  const allRows = pendingRow ? [...existingRows, pendingRow] : [...existingRows];
  const projectedTotals = aggregateExtraChargeRows(allRows);

  return {
    existingRows,
    pendingRow,
    allRows,
    existingTotals,
    projectedTotals,
    hasExisting: existingRows.length > 0,
    hasPending: Boolean(pendingRow),
    showSummary: existingRows.length > 0 || Boolean(pendingRow),
  };
}
